import IconConnectorsResult from '@apollo/icons/small/IconConnectorsResult.svg';
import IconConnectorsSource from '@apollo/icons/small/IconConnectorsSource.svg';
import IconConnectorsTransformation from '@apollo/icons/small/IconConnectorsTransformation.svg';
import IconWarning from '@apollo/icons/small/IconWarning.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Heading,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useState } from 'react';

import { BodyViewer } from '../components/BodyViewer';
import { EmptyState } from '../components/EmptyState';
import { SelectionMappingResponse } from '../shared';

interface Props {
  selection: SelectionMappingResponse;
  showErrorViewFirst?: boolean;
  className?: string;
}

type ActiveView = 'source' | 'result' | 'transformed' | 'errors';

export const ResponseMapping = ({
  className,
  showErrorViewFirst,
  ...props
}: Props) => {
  const [activeView, setActiveView] = useState<ActiveView>(
    showErrorViewFirst ? 'errors' : 'source',
  );
  const { errors = [], ...selection } = props.selection;
  const activeViewData =
    activeView !== 'errors' ? selection[activeView] : undefined;

  return (
    <div className="flex size-full flex-col gap-4">
      <div className="flex justify-between">
        <Heading className="capitalize">{HEADINGS_MAP[activeView]}</Heading>
        <ButtonGroup isAttached>
          <Tooltip label="View original selection">
            <IconButton
              aria-label="View original selection"
              icon={<IconConnectorsSource />}
              size="sm"
              variant={activeView === 'source' ? 'primary' : 'secondary'}
              onClick={() => setActiveView('source')}
            />
          </Tooltip>
          <Tooltip label="View runtime selection">
            <IconButton
              aria-label="View runtime selection"
              icon={<IconConnectorsTransformation />}
              size="sm"
              variant={activeView === 'transformed' ? 'primary' : 'secondary'}
              onClick={() => setActiveView('transformed')}
            />
          </Tooltip>
          <Tooltip label="View result">
            <IconButton
              aria-label="View result"
              icon={<IconConnectorsResult />}
              size="sm"
              variant={activeView === 'result' ? 'primary' : 'secondary'}
              onClick={() => setActiveView('result')}
            />
          </Tooltip>
          <Tooltip label="View warnings">
            <Button
              size="sm"
              variant={
                activeView === 'errors'
                  ? errors.length
                    ? 'destructive'
                    : 'primary'
                  : 'secondary'
              }
              onClick={() => setActiveView('errors')}
              leftIcon={
                <IconWarning
                  className={classnames(
                    activeView !== 'errors' &&
                      errors.length &&
                      '!text-icon-warning',
                  )}
                />
              }
            >
              {errors.length}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </div>
      {activeView === 'errors' ? (
        <ErrorsView errors={errors} />
      ) : (
        <BodyViewer
          className="size-full"
          content={activeViewData}
          kind={typeof activeViewData === 'string' ? 'text' : 'json'}
        />
      )}
    </div>
  );
};

const ErrorsView = ({
  errors,
}: {
  errors: SelectionMappingResponse['errors'];
}) =>
  errors.length ? (
    <Card>
      <CardBody>
        <TableContainer>
          <Table size="condensed" variant="striped">
            <Thead>
              <Th>Message</Th>
              <Th>Path</Th>
              <Th isNumeric>Count</Th>
            </Thead>
            <Tbody>
              {errors.map((error, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tr key={idx}>
                  <Td>{error.message}</Td>
                  <Td>
                    <Text variant="code">{error.path}</Text>
                  </Td>
                  <Td isNumeric>{error.count}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  ) : (
    <EmptyState message="No warnings to display" />
  );

const HEADINGS_MAP: Record<ActiveView, string> = {
  errors: 'Warnings',
  result: 'Result',
  source: 'Original selection',
  transformed: 'Runtime selection',
};
